import React from "react";
import styled from "styled-components";
import { ArrowRight, Play, SquareArrowOutUpRight } from "lucide-react";
import { BotType, LanguageNameSpaces } from "@shared/types";
import { TranslationKeys } from "@shared/common/i18n/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType, getPanelShadow } from "common/NewLook/tokens.ts";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout";
import Button from "common/Buttons/Button";
import IntercomLink from "common/Text/IntercomLink";
import { showIntercomArticle } from "utils/misc.tsx";
import ArtStage from "features/onboarding/art/ArtStage";
import MethodThumb from "features/onboarding/art/MethodThumb";
import { methodForBotType } from "features/onboarding/art/methods";
import {
  BOT_TYPE_TO_ARTICLE_DATA,
  BOT_TYPE_TO_DESCRIPTION,
  BOT_TYPE_TO_MAIN_ARTICLE_ID,
} from "features/bots/common/constants";

interface NewLookBotsIntroductionProps {
  botType: BotType;
  title: string;
  /** The classic modal's animation; the new look draws the bot type's art. */
  animationData?: unknown;
  onWatchVideo: () => void;
}

/**
 * New-look body of the "Welcome to <bot type> bots" modal: the intro with the
 * bot type's illustration (the onboarding / cornix.io art, in its card size so
 * it fills the box legibly), then the guides in one row.
 */
const NewLookBotsIntroduction = ({
  botType,
  title,
  onWatchVideo,
}: NewLookBotsIntroductionProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const articles = BOT_TYPE_TO_ARTICLE_DATA[botType] || [];

  return (
    <>
      <Hero>
        <HeroTexts>
          <HeroTitle>{title}</HeroTitle>
          <HeroDescription>
            {t(
              BOT_TYPE_TO_DESCRIPTION[
                botType
              ] as TranslationKeys<LanguageNameSpaces.BOTS>
            )}
          </HeroDescription>
          <HeroActions>
            <Button type="primary" onClick={onWatchVideo}>
              <Play size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
              {t("modals.watch_video")}
            </Button>
            <Button
              onClick={() =>
                showIntercomArticle(BOT_TYPE_TO_MAIN_ARTICLE_ID[botType])
              }
            >
              <SquareArrowOutUpRight
                size={16}
                strokeWidth={NEW_LOOK_ICON_STROKE}
              />
              {tCommon("labels.learn_more")}
            </Button>
          </HeroActions>
        </HeroTexts>
        <Visual>
          <MethodThumb method={methodForBotType(botType)} />
        </Visual>
      </Hero>
      {articles.length > 0 && (
        <>
          <SectionTitle>{t("modals.everything_you_need")}</SectionTitle>
          <Articles>
            {articles.map((article) => (
              <Article key={article.id}>
                <ArticleTitle>{t(article.title)}</ArticleTitle>
                <ArticleDescription>
                  {t(article.description)}
                </ArticleDescription>
                <ReadMore articleId={article.id}>
                  {t("buttons.read_more")}
                  <ArrowRight size={14} strokeWidth={NEW_LOOK_ICON_STROKE} />
                </ReadMore>
              </Article>
            ))}
          </Articles>
        </>
      )}
    </>
  );
};

export default NewLookBotsIntroduction;

const Hero = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 32px;
  align-items: center;
  padding-bottom: 24px;
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
const HeroTexts = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
// The modal's title: 18 / 600, left-aligned.
const HeroTitle = styled.h2`
  margin: 0;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-size: 18px;
  line-height: 26px;
  font-weight: 520;
  letter-spacing: -0.01em;
`;
const HeroDescription = styled.p`
  margin: 8px 0 0;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 20px;
`;
// The illustration box: as tall as the art at the box's width (it sits
// under the close button, level with the intro).
const Visual = styled(ArtStage)`
  margin-top: 32px;
  padding: 16px;
`;
const SectionTitle = styled.h3`
  margin: 24px 0 12px;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Articles = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
`;
const Article = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  box-shadow: ${(p) => getPanelShadow(p.theme)};
`;
const ArticleTitle = styled.div`
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const ArticleDescription = styled.div`
  flex: 1;
  margin-top: 4px;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const ReadMore = styled(IntercomLink)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 12px;
  ${newLookType.label};
`;
